import type { ReviewItem } from "@plenum/shared/api";
import { Link, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, useBatchAccept, useCapture, useReview, useReviewAction } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Pill } from "../ui/Badge";
import { useToast } from "../ui/Toast";

/**
 * Review queue (PRD §12.4): left, the claim; right, the evidence capture with the quote highlighted.
 * Keyboard: A accept, R reject, E edit, N next, P previous. Batch accept for claims the Verifier
 * passed at ≥0.9. Every decision writes resolved_by and resolved_at server-side.
 */
export function ReviewPage() {
  const search = useSearch({ strict: false }) as { site_id?: number };
  const [siteFilter, setSiteFilter] = useState<number | undefined>(search.site_id);
  const [fieldFilter, setFieldFilter] = useState<string>("");
  const params = useMemo(
    () => ({ status: "pending", site_id: siteFilter, field: fieldFilter || undefined }),
    [siteFilter, fieldFilter],
  );
  const q = useReview(params);
  const act = useReviewAction();
  const batch = useBatchAccept();
  const toast = useToast();
  const items = q.data?.items ?? [];
  const [index, setIndex] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const current = items[Math.min(index, Math.max(0, items.length - 1))];

  useEffect(() => {
    if (index >= items.length) setIndex(Math.max(0, items.length - 1));
  }, [items.length, index]);

  const decide = useCallback(
    async (action: "accept" | "reject" | "edit", value?: unknown) => {
      if (!current) return;
      try {
        const res = await act.mutateAsync({ claimId: current.claim.id, action: { action, value } });
        toast(
          `${current.entity.label} · ${current.claim.field}: ${res.status}${res.applied ? ", applied" : ""}`,
        );
        setEditing(null);
      } catch (e) {
        toast(e instanceof ApiError ? e.message : "Action failed", "danger");
      }
    },
    [act, current, toast],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "a") void decide("accept");
      else if (k === "r") void decide("reject");
      else if (k === "e")
        setEditing((v) => (v === null ? JSON.stringify(current?.claim.value ?? "") : v));
      else if (k === "n" || k === "j") setIndex((i) => Math.min(items.length - 1, i + 1));
      else if (k === "p" || k === "k") setIndex((i) => Math.max(0, i - 1));
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide, items.length, current]);

  // Filter choices come from the whole pending queue when the API provides them (the item list
  // is capped at 200), with the loaded items as the fallback.
  const byField = q.data?.by_field;
  const fields = useMemo(() => {
    if (byField) return Object.entries(byField).sort((a, b) => b[1] - a[1]);
    const seen = new Map<string, number>();
    for (const i of items) seen.set(i.claim.field, (seen.get(i.claim.field) ?? 0) + 1);
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [byField, items]);
  const siteRows = q.data?.sites;
  const sites = useMemo(() => {
    if (siteRows) return siteRows.map((s) => [s.id, `${s.name} (${s.n})`] as [number, string]);
    const m = new Map<number, string>();
    for (const i of items)
      if (i.entity.site_id && i.entity.site_name) m.set(i.entity.site_id, i.entity.site_name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [siteRows, items]);

  return (
    <div className="flex h-full min-h-0">
      <aside
        className="scroll-thin flex w-[380px] shrink-0 flex-col border-r"
        style={{ borderColor: "var(--line)", background: "var(--panel)" }}
      >
        <div className="border-b p-3" style={{ borderColor: "var(--line)" }}>
          <div className="flex items-center justify-between">
            <h1 className="eyebrow" style={{ color: "var(--paper)" }}>
              Review queue <span className="num">{q.data?.counts.proposed ?? 0}</span>
            </h1>
            <button
              type="button"
              className="btn btn-sm"
              disabled={batch.isPending}
              onClick={async () => {
                try {
                  const r = await batch.mutateAsync({ min_conf: 0.9, site_id: siteFilter });
                  toast(
                    `Batch accepted ${r.accepted} verified claim${r.accepted === 1 ? "" : "s"} at ≥ 0.9`,
                  );
                } catch (e) {
                  toast(e instanceof ApiError ? e.message : "Batch failed", "danger");
                }
              }}
              data-testid="batch-accept"
            >
              Batch accept ≥0.9
            </button>
          </div>
          <div className="mt-2 flex gap-2">
            <select
              className="input flex-1"
              value={siteFilter ?? ""}
              onChange={(e) => setSiteFilter(e.target.value ? Number(e.target.value) : undefined)}
              aria-label="Filter by site"
            >
              <option value="">All sites</option>
              {sites.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="input"
              value={fieldFilter}
              onChange={(e) => setFieldFilter(e.target.value)}
              aria-label="Filter by field"
            >
              <option value="">All fields</option>
              {fields.map(([f, n]) => (
                <option key={f} value={f}>
                  {f} ({n})
                </option>
              ))}
            </select>
          </div>
          <p className="mt-2 text-[11px]" style={{ color: "var(--muted-2)" }}>
            Keys: <kbd>A</kbd> accept · <kbd>R</kbd> reject · <kbd>E</kbd> edit · <kbd>N</kbd>/
            <kbd>P</kbd> next/previous
          </p>
        </div>
        <ul className="scroll-thin min-h-0 flex-1 overflow-auto" data-testid="review-list">
          {q.isLoading ? (
            <li className="p-3" style={{ color: "var(--muted)" }}>
              Loading…
            </li>
          ) : null}
          {items.length === 0 && !q.isLoading ? (
            <li className="p-3 text-[12px]" style={{ color: "var(--muted)" }}>
              Nothing to review. Claims arrive from enrichment runs (Phase 4 agents) and reviewer
              edits; use Enrich on a site to start one.
            </li>
          ) : null}
          {items.map((it, i) => (
            <li key={it.claim.id}>
              <button
                type="button"
                className="w-full border-b px-3 py-2 text-left"
                style={{
                  borderColor: "var(--line)",
                  background: i === index ? "var(--panel-3)" : "transparent",
                }}
                onClick={() => setIndex(i)}
                aria-current={i === index ? "true" : undefined}
                data-testid="review-item"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[12px]">{it.entity.label}</span>
                  <Pill tone={it.claim.confidence >= 0.85 ? "ok" : "muted"}>
                    {Math.round(it.claim.confidence * 100)} %
                  </Pill>
                </div>
                <div
                  className="flex items-center gap-2 text-[11px]"
                  style={{ color: "var(--muted)" }}
                >
                  <span className="font-medium" style={{ color: "var(--paper)" }}>
                    {it.claim.field}
                  </span>
                  <span className="num truncate">
                    {typeof it.claim.value === "string"
                      ? it.claim.value
                      : JSON.stringify(it.claim.value)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className="scroll-thin min-w-0 flex-1 overflow-auto p-5">
        {current ? (
          <ClaimDetail
            item={current}
            editing={editing}
            onEditChange={setEditing}
            onDecide={decide}
            busy={act.isPending}
          />
        ) : (
          <p style={{ color: "var(--muted)" }}>Select a claim.</p>
        )}
      </div>
    </div>
  );
}

function ClaimDetail({
  item,
  editing,
  onEditChange,
  onDecide,
  busy,
}: {
  item: ReviewItem;
  editing: string | null;
  onEditChange: (v: string | null) => void;
  onDecide: (a: "accept" | "reject" | "edit", value?: unknown) => Promise<void>;
  busy: boolean;
}) {
  const c = item.claim;
  const capture = useCapture(item.capture_url);
  const verifier = (c.verifier ?? {}) as {
    rationale?: string;
    decision?: string;
    conflicts?: string[];
    evidence?: Array<{
      url: string;
      passed: boolean;
      quote_found: boolean;
      fresh: boolean;
      http_status: number | null;
    }>;
    plausibility?: Record<string, boolean>;
  };
  return (
    <div className="flex flex-col gap-4" data-testid="claim-detail">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-[16px] font-semibold">{item.entity.label}</h2>
          {item.entity.site_id ? (
            <Link
              to="/sites/$id"
              params={{ id: String(item.entity.site_id) }}
              search={{ tab: "evidence" }}
              className="text-[12px]"
            >
              open site
            </Link>
          ) : null}
        </div>
        <div
          className="mt-1 flex flex-wrap items-center gap-2 text-[12px]"
          style={{ color: "var(--muted)" }}
        >
          <span>{c.field}</span>
          <Pill tone={c.confidence >= 0.85 ? "ok" : "muted"}>
            {Math.round(c.confidence * 100)} %
          </Pill>
          <span>{c.extracted_by}</span>
          <span>{fmtDate(c.created_at)}</span>
          {item.queue ? <Pill>{item.queue.reason}</Pill> : null}
        </div>
      </header>
      <section className="grid gap-3 md:grid-cols-2">
        <div
          className="rounded border p-3"
          style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
        >
          <div className="eyebrow mb-1">Proposed value</div>
          {editing === null ? (
            <pre className="num whitespace-pre-wrap text-[13px]" data-testid="claim-value">
              {typeof c.value === "string" ? c.value : JSON.stringify(c.value, null, 2)}
            </pre>
          ) : (
            <div className="flex flex-col gap-2">
              <textarea
                className="input num h-24 w-full"
                value={editing}
                onChange={(e) => onEditChange(e.target.value)}
                aria-label="Edited value (JSON or text)"
                data-testid="edit-value"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => onDecide("edit", parseLoose(editing))}
                  disabled={busy}
                >
                  Save edit
                </button>
                <button type="button" className="btn btn-sm" onClick={() => onEditChange(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {verifier.rationale ? (
            <p className="mt-2 text-[12px]" style={{ color: "var(--muted)" }}>
              Verifier: {verifier.rationale}
            </p>
          ) : null}
          {verifier.conflicts && verifier.conflicts.length > 0 ? (
            <ul className="mt-1 list-disc pl-4 text-[12px]" style={{ color: "var(--danger)" }}>
              {verifier.conflicts.map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => onDecide("accept")}
              disabled={busy}
              data-testid="accept"
            >
              Accept (A)
            </button>
            <button
              type="button"
              className="btn btn-sm"
              style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
              onClick={() => onDecide("reject")}
              disabled={busy}
              data-testid="reject"
            >
              Reject (R)
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onEditChange(JSON.stringify(c.value))}
              disabled={busy}
              data-testid="edit"
            >
              Edit (E)
            </button>
          </div>
        </div>
        <div
          className="rounded border p-3"
          style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
        >
          <div className="eyebrow mb-1">Source</div>
          <a href={c.source.url} target="_blank" rel="noreferrer" className="break-all text-[12px]">
            {c.source.title ?? c.source.url}
          </a>
          <div className="mt-1 text-[11px]" style={{ color: "var(--muted)" }}>
            {c.source.source_type} · retrieved {fmtDate(c.source.retrieved_at)}
          </div>
          <blockquote
            className="mt-2 border-l-2 pl-2 text-[12px]"
            style={{ borderColor: "var(--gold)" }}
          >
            “{c.quote}”
          </blockquote>
          {verifier.evidence ? (
            <ul className="mt-2 flex flex-col gap-1 text-[11px]">
              {verifier.evidence.map((e) => (
                <li key={e.url} className="flex flex-wrap gap-1">
                  <Pill tone={e.passed ? "ok" : "danger"}>{e.passed ? "verified" : "failed"}</Pill>
                  <span style={{ color: "var(--muted)" }}>
                    fetch {e.http_status ?? "—"} · quote {e.quote_found ? "found" : "missing"} ·{" "}
                    {e.fresh ? "fresh" : "stale"}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>
      <section>
        <div className="eyebrow mb-1">Evidence document</div>
        {item.capture_url ? (
          capture.isLoading ? (
            <p style={{ color: "var(--muted)" }}>Loading capture…</p>
          ) : capture.data ? (
            <Highlighted text={capture.data} quote={c.quote ?? ""} />
          ) : (
            <p style={{ color: "var(--muted)" }}>
              Capture unavailable; open the source link above.
            </p>
          )
        ) : (
          <p className="text-[12px]" style={{ color: "var(--muted)" }}>
            No stored capture for this source (reviewer edits and pipeline claims link straight to
            the URL). Enrichment agents store every fetched page in R2 so the quote can be checked
            here.
          </p>
        )}
      </section>
    </div>
  );
}

function parseLoose(text: string): unknown {
  const t = text.trim();
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

function Highlighted({ text, quote }: { text: string; quote: string }) {
  const norm = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
  const idx = quote ? norm(text).indexOf(norm(quote)) : -1;
  // Map the normalised index back approximately by searching the raw text with collapsed whitespace.
  let before = text;
  let match = "";
  let after = "";
  if (idx >= 0) {
    const re = new RegExp(
      quote
        .trim()
        .split(/\s+/)
        .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("\\s+"),
      "i",
    );
    const m = re.exec(text);
    if (m) {
      before = text.slice(0, m.index);
      match = m[0];
      after = text.slice(m.index + m[0].length);
    }
  }
  return (
    <pre
      className="scroll-thin max-h-[50vh] overflow-auto whitespace-pre-wrap rounded border p-3 text-[12px] leading-relaxed"
      style={{ borderColor: "var(--line)", background: "var(--ink)" }}
      data-testid="capture"
    >
      {before}
      {match ? (
        <mark
          style={{ background: "var(--gold)", color: "var(--ink)" }}
          data-testid="quote-highlight"
        >
          {match}
        </mark>
      ) : null}
      {after}
    </pre>
  );
}
